import styled, { useTheme } from "styled-components/native";
import React, { memo } from "react";
import { Button, Spacer } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { PopupProps, messageType } from "@components/molecules/PopUp/types";
import {
  KeyboardAvoidingView,
  Platform,
  TouchableWithoutFeedback,
  Keyboard,
  useWindowDimensions,
  ScrollView,
} from "react-native";
import RenderHtml from "react-native-render-html";
import { SpaceType } from "@components/atoms/Spacer/types";
import PopupTitleContainer from "@scenes/requests/utils/PopupTitleContainer";
import { View } from "@gluestack-ui/themed";
import { VDBIcons } from "@utils/VDBIcons";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { DefaultTheme } from "styled-components/native";
import { css } from "styled-components/native";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

const BOLD_FONT_FAMILY = "Roboto-Bold";

const BOLD_TEXT_STYLE = {
  fontFamily: BOLD_FONT_FAMILY,
  fontWeight: "normal" as const,
};

const HTML_BODY_CLASS = "vdb-popup-html";

const WEB_BOLD_STYLE = `.${HTML_BODY_CLASS} b, .${HTML_BODY_CLASS} strong { font-family: ${BOLD_FONT_FAMILY}; font-weight: 400; }`;

const Popup = ({
  title,
  buttons,
  type,
  HtmlSource,
  styleKey,
  colorKey,
  isLoading,
  hasChildren,
  children,
  isFromSavedSearch,
  maxWidth: maxWidthProp,
  onClose,
  showCloseButton = false,
  applyMaxHeight = false,
  visible = true,
  onDismiss,
}: PopupProps) => {
  const PopUpIcon =
    type === messageType.Error
      ? VDBIcons.WARNING
      : type === messageType.Success
      ? VDBIcons.CHECKMARK
      : type === messageType.Warning
      ? VDBIcons.MESSAGE
      : null;
  const { deviceWidth: width } = useIsFoldable();
  const { height } = useWindowDimensions();
  const theme = useTheme();
  const TouchableWithoutFeedbackView = applyMaxHeight
    ? React.Fragment
    : TouchableWithoutFeedback;
  const maxWidth =
    theme.isLargeScreen || theme.isTablet ? maxWidthProp || 343 : width;
  const typography = useTypographyStore();
  return (
    <Overlay
      animationType="fade"
      transparent={true}
      visible={visible}
      onDismiss={onDismiss}
      supportedOrientations={["portrait", "landscape"]}
    >
      <TouchableWithoutFeedbackView
        {...(!applyMaxHeight && { onPress: () => Keyboard.dismiss })}
      >
        <Box>
          <KeyboardAvoidingView
            behavior={Platform.OS === "android" ? undefined : "padding"}
            style={{ alignItems: "center" }}
          >
            <Container maxWidth={maxWidth} theme={theme}>
              <BasicPopup>
                {title && (
                  <PopupTitleContainer
                    type={type}
                    PopUpIcon={PopUpIcon}
                    title={title}
                    onClose={onClose}
                    showCloseButton={showCloseButton}
                  />
                )}
                <ScrollView>
                  {((hasChildren && children) || HtmlSource) && (
                    <MessageBody
                      isFromSavedSearch={isFromSavedSearch}
                      maxHeight={height}
                      applyMaxHeight={applyMaxHeight}
                    >
                      {Platform.OS === "web" ? (
                        <>
                          <style
                            dangerouslySetInnerHTML={{
                              __html: WEB_BOLD_STYLE,
                            }}
                          />
                          <div
                            className={HTML_BODY_CLASS}
                            style={{
                              ...typography[styleKey],
                              color: colorKey
                                ? theme[colorKey]
                                : theme["core-05"],
                            }}
                            dangerouslySetInnerHTML={{
                              __html: HtmlSource?.html,
                            }}
                          />
                        </>
                      ) : (
                        <>
                          {HtmlSource && (
                            <RenderHtml
                              contentWidth={width}
                              source={HtmlSource}
                              tagsStyles={{
                                body: {
                                  ...typography[styleKey],
                                  color: colorKey
                                    ? theme[colorKey]
                                    : theme["core-05"],
                                },

                                a: {
                                  color: theme["link-static"],
                                  textDecorationLine: "none",
                                },
                                b: BOLD_TEXT_STYLE,
                                strong: BOLD_TEXT_STYLE,
                              }}
                            />
                          )}
                        </>
                      )}
                      {hasChildren && <>{children}</>}
                    </MessageBody>
                  )}
                </ScrollView>
                <LineView />
                <Controls>
                  {buttons.map((btn, index) => (
                    <React.Fragment key={index}>
                      <Button
                        type={btn.type as ButtonType}
                        loading={btn.isLoadingButton ? isLoading : btn.loading}
                        category={
                          btn.category
                            ? (btn.category as ButtonCategory)
                            : ButtonCategory.default
                        }
                        title={btn.title}
                        onPress={btn.onPressAction}
                        disabled={btn.disabled}
                        textTypographyStyle="H3-M"
                      />
                      {index !== buttons.length - 1 && (
                        <Spacer size={SpaceType.Double} />
                      )}
                    </React.Fragment>
                  ))}
                </Controls>
              </BasicPopup>
            </Container>
          </KeyboardAvoidingView>
        </Box>
      </TouchableWithoutFeedbackView>
    </Overlay>
  );
};

const Overlay = styled.Modal``;

const Box = styled(View)`
  flex: 1;
  justify-content: center;
  border-radius: ${SpaceType.Basic}px;
  background-color: rgba(0, 0, 0, 0.5);
`;

const BasicPopup = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
`;
const Container = styled.View<{ theme: DefaultTheme; maxWidth: string }>`
  max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px` : "100%")};
  padding-horizontal: ${({ theme }) => (theme.isMobile ? "16px" : "0px")};
  width: 100%;
`;

const Controls = styled.View`
  padding: ${SpaceType.Big}px ${SpaceType.Double}px;
  flex-direction: column;
  width: 100%;
`;

const MessageBody = styled.View<{
  isFromSavedSearch?: boolean;
  height: number;
  applyMaxHeight: boolean;
}>`
  display: flex;
  ${({
    maxHeight,
    applyMaxHeight,
  }: {
    maxHeight: number;
    applyMaxHeight: boolean;
  }) =>
    maxHeight &&
    applyMaxHeight &&
    css`
      max-height: ${({ theme }) =>
        theme?.isMobile ? maxHeight * 0.65 : maxHeight * 0.7}px;
    `}

  padding: ${({ isFromSavedSearch }) =>
    isFromSavedSearch
      ? `${SpaceType.Double}px ${SpaceType.Double}px;`
      : `${SpaceType.Big}px ${SpaceType.Double}px;`};
  flex-direction: column;
`;

const LineView = styled.View`
  height: 1px;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
`;

export default memo(Popup);
